import { TagAction, TagAlert, TagError } from "../types/Tag.types";
import { MTLibrary, MTPayload, WindowWithTracking } from "../types/MT.types";
import { getEnvironment } from "./getEnvironment";

type MTMethodNames = keyof MTLibrary;
type MTMethodArgs = {
  [K in MTMethodNames]: Parameters<MTLibrary[K]>;
};

type MTQueueItem = {
  methodName: MTMethodNames;
  args: MTMethodArgs[MTMethodNames];
};

const trackMethodQueue: MTQueueItem[] = [];

const track = <T extends MTMethodNames>(
  methodName: T,
  ...args: MTMethodArgs[T]
) => {
  const environment = getEnvironment(window.location.hostname);

  if (environment === "localhost") {
    console.log(methodName, args);
    return;
  }
  const { MT } = window as WindowWithTracking;

  if (MT) {
    flushQueue(MT);

    if (typeof MT[methodName] !== "function") {
      console.error(`Invalid method name: ${methodName}`);

      return;
    }
    try {
      (MT[methodName] as (...a: typeof args) => void)(...args);
    } catch (error) {
      // Swallowing errors to reduce noise
      console.error(error);
    }
  } else {
    trackMethodQueue.push({ methodName, args });
  }
};

const flushQueue = (MT: MTLibrary) => {
  while (trackMethodQueue.length > 0) {
    const nextInQueue = trackMethodQueue.shift();
    if (!nextInQueue) continue;
    const { methodName, args } = nextInQueue;
    (MT[methodName] as (...a: typeof args) => void)?.(...args);
  }
};

export const mt = {
  trackAction: ({ actionType, actionName }: TagAction) => {
    track("trackAction", actionType, actionName);
  },
  trackAlert: ({ alertType, alertDetails, payload }: TagAlert) => {
    track("trackAlert", alertType, alertDetails, payload);
  },
  trackError: ({ errorType, errorDetails, payload }: TagError) => {
    track("trackError", errorType, errorDetails, payload);
  },
  trackEvent: (payload?: MTPayload) => {
    track("trackEvent", payload);
  },
  trackPopup: (name: string) => {
    track("trackPopup", name);
  },
  trackPopupClose: () => {
    track("trackPopupClose");
  },
  trackView: (payload?: MTPayload) => {
    track("trackView", payload);
  },
};
